import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  ContractBenefitPayload,
  createContractBenefitApi,
  deleteContractBenefitApi,
  DpBenefitType,
  DpContractBenefit,
  listBenefitTypesApi,
  listContractBenefitsApi,
  updateContractBenefitApi,
} from "../api/benefitsApi";
import { DEDUCTION_CALC_MODE_LABELS, DpDeductionCalcMode } from "../api/payrollApi";
import { formatCurrency, formatPercentValue } from "../utils/format";

const emptyForm = {
  benefitTypeId: "",
  startMonth: "",
  endMonth: "",
  employerCost: "",
  calcMode: "VALOR_FIXO" as DpDeductionCalcMode,
  percent: "",
  amount: "",
  notes: "",
};

/** 'YYYY-MM-01' (data civil ISO) → 'MM/AAAA'. */
const monthLabel = (iso: string | null) => {
  if (!iso) return null;
  const [year, month] = iso.slice(0, 7).split("-");
  return `${month}/${year}`;
};

const describeDeduction = (b: DpContractBenefit) => {
  if (!b.calcMode) return "—";
  return b.calcMode === "VALOR_FIXO"
    ? formatCurrency(b.amount)
    : `${formatPercentValue(b.percent)} ${DEDUCTION_CALC_MODE_LABELS[b.calcMode].replace(/^% /, "")}`;
};

const describePeriod = (b: DpContractBenefit) => {
  const start = monthLabel(b.startMonth);
  if (b.endMonth) return `${start} a ${monthLabel(b.endMonth)}`;
  return `desde ${start}`;
};

const thisMonth = () => new Date().toISOString().slice(0, 7);

type Props = { token: string; contractId: string; readOnly: boolean };

/**
 * Card "Outros benefícios" da aba Contrato: plano odontológico, Gympass e o que
 * mais o DP cadastrar em Cadastros → Benefícios. Quando o benefício tem rubrica,
 * o desconto do colaborador entra na folha de conferência nos meses da vigência.
 */
export default function ContractBenefitsCard({ token, contractId, readOnly }: Props) {
  const [rows, setRows] = useState<DpContractBenefit[]>([]);
  const [types, setTypes] = useState<DpBenefitType[]>([]);
  const [modal, setModal] = useState<{ row?: DpContractBenefit } | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listContractBenefitsApi(token, contractId));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os benefícios do colaborador.");
    }
  }, [token, contractId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (readOnly) return;
    listBenefitTypesApi(token)
      .then(setTypes)
      .catch(() => setTypes([]));
  }, [token, readOnly]);

  const selectedType = useMemo(() => types.find((t) => t.id === form.benefitTypeId) ?? null, [types, form.benefitTypeId]);
  // Ao editar, o benefício atual aparece mesmo se foi inativado depois.
  const selectableTypes = useMemo(
    () => types.filter((t) => t.isActive || t.id === modal?.row?.benefitTypeId),
    [types, modal]
  );
  const hasDeduction = !!selectedType?.item;

  const openModal = (row?: DpContractBenefit) => {
    setForm(
      row
        ? {
            benefitTypeId: row.benefitTypeId,
            startMonth: row.startMonth.slice(0, 7),
            endMonth: row.endMonth ? row.endMonth.slice(0, 7) : "",
            employerCost: row.employerCost === null ? "" : String(Number(row.employerCost)),
            calcMode: row.calcMode ?? "VALOR_FIXO",
            percent: row.percent === null ? "" : String(Number(row.percent)),
            amount: row.amount === null ? "" : String(Number(row.amount)),
            notes: row.notes ?? "",
          }
        : { ...emptyForm, startMonth: thisMonth() }
    );
    setModal({ row });
  };

  // Escolher o benefício puxa as sugestões do catálogo (custo e desconto).
  const chooseType = (benefitTypeId: string) => {
    const type = types.find((t) => t.id === benefitTypeId);
    setForm((f) => ({
      ...f,
      benefitTypeId,
      employerCost: type?.defaultEmployerCost !== null && type?.defaultEmployerCost !== undefined ? String(Number(type.defaultEmployerCost)) : f.employerCost,
      calcMode: "VALOR_FIXO",
      percent: "",
      amount: type?.defaultEmployeeAmount !== null && type?.defaultEmployeeAmount !== undefined ? String(Number(type.defaultEmployeeAmount)) : "",
    }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (!form.benefitTypeId) return toast.error("Escolha o benefício.");
    if (!form.startMonth) return toast.error("Informe a primeira competência.");
    const isFixed = form.calcMode === "VALOR_FIXO";
    const payload: ContractBenefitPayload = {
      benefitTypeId: form.benefitTypeId,
      startMonth: form.startMonth,
      endMonth: form.endMonth || null,
      employerCost: form.employerCost.trim() === "" ? null : Number(form.employerCost),
      // Valores de desconto só quando o benefício tem rubrica: o backend recusa o contrário.
      calcMode: hasDeduction ? form.calcMode : null,
      amount: hasDeduction && isFixed ? Number(form.amount) : null,
      percent: hasDeduction && !isFixed ? Number(form.percent) : null,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (modal.row) await updateContractBenefitApi(token, modal.row.id, payload);
      else await createContractBenefitApi(token, contractId, payload);
      toast.success(
        hasDeduction ? "Benefício salvo. O desconto vale nas competências abertas ao recalcular." : "Benefício salvo."
      );
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o benefício.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: DpContractBenefit) => {
    if (!window.confirm(`Excluir o benefício "${row.benefitType.name}" deste colaborador?`)) return;
    try {
      await deleteContractBenefitApi(token, row.id);
      toast.success("Benefício excluído.");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao excluir o benefício.");
    }
  };

  return (
    <div className="dpSection" style={{ marginBottom: 16 }}>
      <div className="dpHeader" style={{ marginBottom: 12 }}>
        <h3 className="dpSection__title" style={{ margin: 0 }}>
          Outros benefícios
        </h3>
        {!readOnly ? (
          <button className="dpBtn dpBtn--secondary" onClick={() => openModal()}>
            Atribuir benefício
          </button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="dpFormHint">
          Nenhum benefício além de VT, VR e VA. Os benefícios disponíveis são cadastrados em Cadastros → Benefícios.
        </p>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Benefício</th>
                <th>Período</th>
                <th>Custo (empresa)</th>
                <th>Desconto (colaborador)</th>
                {!readOnly ? <th style={{ width: 160 }}>Ações</th> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.benefitType.name}
                    {row.notes ? <div className="dpFormHint">{row.notes}</div> : null}
                  </td>
                  <td>{describePeriod(row)}</td>
                  <td>{row.employerCost !== null ? `${formatCurrency(row.employerCost)}/mês` : "—"}</td>
                  <td>
                    {describeDeduction(row)}
                    {row.benefitType.item ? <div className="dpFormHint">{row.benefitType.item.name}</div> : null}
                  </td>
                  {!readOnly ? (
                    <td>
                      <div className="dpTable__actions">
                        <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openModal(row)}>
                          Editar
                        </button>
                        <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => remove(row)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.row ? "Editar benefício" : "Atribuir benefício"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="cbType">Benefício *</label>
                  <select
                    id="cbType"
                    className="dpSelect"
                    value={form.benefitTypeId}
                    onChange={(e) => chooseType(e.target.value)}
                    disabled={saving}
                  >
                    <option value="">Selecione</option>
                    {selectableTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.item ? "" : " (custeado pela empresa)"}
                      </option>
                    ))}
                  </select>
                  <span className="dpFormHint">
                    {selectedType?.description || "Falta algum? Cadastre em Cadastros → Benefícios."}
                  </span>
                </div>
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="cbStart">Primeira competência *</label>
                    <input
                      id="cbStart"
                      type="month"
                      className="dpInput"
                      value={form.startMonth}
                      onChange={(e) => setForm({ ...form, startMonth: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="dpFormGroup">
                    <label htmlFor="cbEnd">Última competência</label>
                    <input
                      id="cbEnd"
                      type="month"
                      className="dpInput"
                      value={form.endMonth}
                      onChange={(e) => setForm({ ...form, endMonth: e.target.value })}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Vazio = em aberto. Para encerrar, informe o último mês.</span>
                  </div>
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="cbEmployer">Custo mensal para a empresa (R$)</label>
                  <input
                    id="cbEmployer"
                    type="number"
                    step="0.01"
                    min="0"
                    className="dpInput"
                    value={form.employerCost}
                    onChange={(e) => setForm({ ...form, employerCost: e.target.value })}
                    disabled={saving}
                  />
                  <span className="dpFormHint">Informativo: não entra na folha.</span>
                </div>
                {hasDeduction ? (
                  <fieldset className="dpBenefitBlock" disabled={saving}>
                    <strong>Desconto do colaborador na folha</strong>
                    <div className="dpFormGrid dpFormGrid--two">
                      <div className="dpFormGroup">
                        <label htmlFor="cbMode">Forma de cálculo *</label>
                        <select
                          id="cbMode"
                          className="dpSelect"
                          value={form.calcMode}
                          onChange={(e) => setForm({ ...form, calcMode: e.target.value as DpDeductionCalcMode })}
                        >
                          {(Object.keys(DEDUCTION_CALC_MODE_LABELS) as DpDeductionCalcMode[]).map((mode) => (
                            <option key={mode} value={mode}>
                              {DEDUCTION_CALC_MODE_LABELS[mode]}
                            </option>
                          ))}
                        </select>
                      </div>
                      {form.calcMode === "VALOR_FIXO" ? (
                        <div className="dpFormGroup">
                          <label htmlFor="cbAmount">Valor mensal (R$) *</label>
                          <input
                            id="cbAmount"
                            type="number"
                            step="0.01"
                            min="0.01"
                            className="dpInput"
                            value={form.amount}
                            onChange={(e) => setForm({ ...form, amount: e.target.value })}
                          />
                        </div>
                      ) : (
                        <div className="dpFormGroup">
                          <label htmlFor="cbPercent">Percentual (%) *</label>
                          <input
                            id="cbPercent"
                            type="number"
                            step="0.0001"
                            min="0.0001"
                            max="100"
                            className="dpInput"
                            value={form.percent}
                            onChange={(e) => setForm({ ...form, percent: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                    <span className="dpFormHint">Sai pela rubrica "{selectedType?.item?.name}" nos meses da vigência.</span>
                  </fieldset>
                ) : selectedType ? (
                  <p className="dpFormHint" style={{ margin: 0 }}>
                    Benefício custeado pela empresa: nada é descontado do colaborador.
                  </p>
                ) : null}
                <div className="dpFormGroup">
                  <label htmlFor="cbNotes">Observação</label>
                  <textarea
                    id="cbNotes"
                    className="dpTextarea"
                    maxLength={500}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    disabled={saving}
                  />
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
